/**
 * SignaturePreview — live Gmail-compose-style rendering of the compiled
 * signature. Light/dark background toggle (dark-mode sanity check),
 * desktop/mobile width toggle, the 10k character meter, click-to-select
 * (annotated compile), and purple glow on blocks FutureBot just touched.
 */

import React, { useMemo } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  Flex,
  HStack,
  Progress,
  Text,
  Tooltip,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiMonitor, FiMoon, FiSmartphone, FiSun } from 'react-icons/fi';
import { compileSignatureHtml } from './compileSignatureHtml';

export default function SignaturePreview({ spec, stats, agentTouchedIds, selectedBlockId, onSelectBlock }) {
  const [mode, setMode] = React.useState('light');
  const [width, setWidth] = React.useState('desktop');

  const panelBG = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');

  const html = useMemo(() => compileSignatureHtml(spec, { annotate: true }), [spec]);

  const glowCss = useMemo(() => {
    const rules = [];
    (agentTouchedIds ? Array.from(agentTouchedIds) : []).forEach((id) => {
      rules.push(`.sig-preview [data-sig-block="${id}"]{box-shadow:0 0 0 2px rgba(159,122,234,.85);border-radius:3px;animation:sigStudioGlow 2.6s ease-out;}`);
    });
    if (selectedBlockId) {
      rules.push(`.sig-preview [data-sig-block="${selectedBlockId}"]{outline:1.5px solid rgba(159,122,234,.9);outline-offset:2px;border-radius:2px;}`);
    }
    return rules.join('\n');
  }, [agentTouchedIds, selectedBlockId]);

  const handleClick = (e) => {
    const target = e.target.closest && e.target.closest('[data-sig-block]');
    if (target && onSelectBlock) {
      e.preventDefault();
      onSelectBlock(target.getAttribute('data-sig-block'));
    } else if (onSelectBlock) {
      onSelectBlock(null);
    }
  };

  const dark = mode === 'dark';
  const composeBG = dark ? '#202124' : '#ffffff';
  const composeText = dark ? '#bdc1c6' : '#5f6368';
  const frameWidth = width === 'mobile' ? '375px' : '100%';

  const meterColor = stats?.overLimit ? 'red' : stats?.nearLimit ? 'orange' : 'green';

  return (
    <Box bg={panelBG} borderWidth="1px" borderColor={borderColor} borderRadius="lg" overflow="hidden">
      <style>{`
        @keyframes sigStudioGlow { 0% { box-shadow: 0 0 0 4px rgba(159,122,234,.9); } 100% { box-shadow: 0 0 0 2px rgba(159,122,234,0); } }
        .sig-preview [data-sig-block] { cursor: pointer; }
        .sig-preview [data-sig-block]:hover { outline: 1px dashed rgba(159,122,234,.55); outline-offset: 2px; border-radius: 2px; }
        .sig-preview a { pointer-events: none; }
        ${glowCss}
      `}</style>

      {/* Toolbar */}
      <Flex px={4} py={2} borderBottomWidth="1px" borderColor={borderColor} align="center" wrap="wrap">
        <Text fontSize="sm" fontWeight="bold" mr={3}>Live preview</Text>
        <ButtonGroup size="xs" isAttached variant="outline" mr={2}>
          <Tooltip label="Light inbox"><Button leftIcon={<FiSun />} onClick={() => setMode('light')} isActive={!dark}>Light</Button></Tooltip>
          <Tooltip label="Dark inbox — check that colors and logos survive"><Button leftIcon={<FiMoon />} onClick={() => setMode('dark')} isActive={dark}>Dark</Button></Tooltip>
        </ButtonGroup>
        <ButtonGroup size="xs" isAttached variant="outline">
          <Tooltip label="Desktop width"><Button leftIcon={<FiMonitor />} onClick={() => setWidth('desktop')} isActive={width === 'desktop'}>Desktop</Button></Tooltip>
          <Tooltip label="Phone width — signatures must fit ~375px"><Button leftIcon={<FiSmartphone />} onClick={() => setWidth('mobile')} isActive={width === 'mobile'}>Mobile</Button></Tooltip>
        </ButtonGroup>
        <Box flex={1} />
        <HStack spacing={2} minW="190px">
          <Progress value={Math.min(100, stats?.pctOfLimit || 0)} size="xs" colorScheme={meterColor} flex={1} borderRadius="full" />
          <Text fontSize="xs" color={stats?.overLimit ? 'red.400' : mutedColor} whiteSpace="nowrap">
            {(stats?.chars || 0).toLocaleString()} / {(stats?.limit || 10000).toLocaleString()}
          </Text>
        </HStack>
      </Flex>

      {/* Gmail compose mock */}
      <Box p={{ base: 3, md: 6 }} bg={dark ? 'gray.900' : 'gray.100'}>
        <Box
          maxW={frameWidth}
          mx="auto"
          borderRadius="8px"
          overflow="hidden"
          boxShadow="0 8px 24px rgba(0,0,0,.18)"
          transition="max-width .25s ease"
        >
          <Flex bg={dark ? '#111111' : '#404040'} color="white" px={4} py={2} align="center">
            <Text fontSize="xs" fontWeight="semibold">New Message</Text>
          </Flex>
          <Box bg={composeBG} px={4} pt={2} pb={5} transition="background .2s ease">
            <Box borderBottomWidth="1px" borderColor={dark ? 'whiteAlpha.200' : 'gray.100'} py={1.5}>
              <Text fontSize="xs" color={composeText}>To: A client</Text>
            </Box>
            <Box borderBottomWidth="1px" borderColor={dark ? 'whiteAlpha.200' : 'gray.100'} py={1.5} mb={3}>
              <Text fontSize="xs" color={composeText}>Subject: Your loan update</Text>
            </Box>
            <Text fontSize="sm" color={composeText} mb={1}>Hi Alex,</Text>
            <Text fontSize="sm" color={composeText} mb={4}>Great news — see the details below.</Text>
            <Text fontSize="sm" color={composeText} mb={3}>Best,</Text>
            {/* eslint-disable-next-line react/no-danger */}
            <div className="sig-preview" onClick={handleClick} dangerouslySetInnerHTML={{ __html: html || '' }} />
            {!html && (
              <Text fontSize="sm" color={composeText} fontStyle="italic">
                Your signature is empty — add blocks on the left, pick a template, or ask FutureBot to build one.
              </Text>
            )}
          </Box>
        </Box>
        <Text fontSize="10px" color={mutedColor} textAlign="center" mt={2}>
          Click any part of the signature to edit it. Always send yourself a real test email after installing.
        </Text>
      </Box>
    </Box>
  );
}
